import { useLoginStore } from '@/login/stores/useLoginStore';
import { ResErr, ResgateManager } from '@/newApi/services/ResgateManager';
import type { Collection } from '@/newApi/services/ResgateManager';
import type { QueryOptions, UseMutationOptions } from '@tanstack/vue-query';
import { queryOptions, useQueryClient } from '@tanstack/vue-query';
import type { ScimApiKey } from '@sysarb/sysarb-types/services/v1/sso/scim/scimApiKey';
import type { ScimGroupMemberModel, ScimRoleMappingModel } from '@sysarb/sysarb-types/services/v1/sso/scim/scimRoleMapping';
import type { ScimSettings } from '@sysarb/sysarb-types-v2/services/v1/sso/scim/scimSettings';
import type { RoleProvisioningType } from '@sysarb/sysarb-types/services/v1/sso/scim/roleProvisioningType';
import type { MatchEmployeeAttribute } from '@sysarb/sysarb-types-v2/services/v1/sso/scim/matchEmployeeAttribute';
import type { Role } from '@sysarb/sysarb-types/service-base/enums';
import { RestManager } from '@/newApi/services/rest/RestManager';
import { getApiHost } from '@/appSettings';

export type ScimRoleMappingFilterData = {
    roles: (Role | null)[];
    orgIds: (string | null)[];
};

export type ScimUserBasedModel = {
    id: string;
    subdomain: string;
    userId: string;
    user: { Username: string } | null;
    role: Role | null;
    organisationNodeId: string | null;
    createdAt: string;
};

const ssoService = new RestManager(`https://${getApiHost()}/sso-service`);

type ScimOptions = {
    queries: {
        getScimApiKeys: () => QueryOptions<Collection<ScimApiKey>, Error>;
        getScimSettings: () => QueryOptions<ScimSettings, Error>;
        getScimGroupMembers: (id: string) => QueryOptions<Collection<ScimGroupMemberModel>, Error>;
        getScimGroupStatus: () => QueryOptions<{ total: number, notDone: number }, Error>;
        getScimRoleMappingFilterData: () => QueryOptions<ScimRoleMappingFilterData, Error>;
        getScimUserBasedFilterData: () => QueryOptions<ScimRoleMappingFilterData, Error>;
    };
    mutations: {
        createScimApiKey: () => UseMutationOptions<ScimApiKey, Error, void, void>;
        deleteScimApiKey: () => UseMutationOptions<void, Error, { id: string; }, void>;
        setScimSettings: () => UseMutationOptions<ScimSettings, Error, { roleProvisioningByScim: boolean }, void>;
        setRoleProvisioningType: () => UseMutationOptions<ScimSettings, Error, { roleProvisioningType: RoleProvisioningType }, void>;
        setMatchEmployeeAttribute: () => UseMutationOptions<ScimSettings, Error, { matchEmployeeAttribute: MatchEmployeeAttribute }, void>;
        deleteScimIntegration: () => UseMutationOptions<void, Error, void, void>;
        updateScimMapping: () => UseMutationOptions<void, Error, { id: string; role?: Role | null; organisationNodeId?: string | null }, void>;
        bulkEditScimMapping: () => UseMutationOptions<void, Error, { ids: string[]; role?: Role | null; organisationNodeId?: string | null }, void>;
        checkScimMappingDuplicate: () => UseMutationOptions<ScimRoleMappingModel | null, Error, { role: Role | null; organisationNodeId: string | null; roleMappingId: string }, void>;
    };
};

export const scim: ScimOptions = {
    queries: {
        getScimApiKeys: (): QueryOptions<Collection<ScimApiKey>, Error> => {
            const { instanceTopic } = useLoginStore();
            const topic = `v1.system.scim-api-keys.${instanceTopic}`;

            return queryOptions<Collection<ScimApiKey>, Error>({
                meta: {
                    subscribe: true,
                    type: 'resgate',
                    resourceId: topic,
                },
                queryKey: ['scim-api-keys', topic],
                queryFn: async (): Promise<Collection<ScimApiKey>> => {
                    const resgate = ResgateManager.getInstance();
                    const res = await resgate.collection<ScimApiKey>(topic);
                    if (res instanceof ResErr) throw new Error(res.message);
                    return res;
                },
            });
        },
        getScimSettings: (): QueryOptions<ScimSettings, Error> => {
            const { instanceTopic } = useLoginStore();
            const topic = `v1.scim-settings.${instanceTopic}`;

            return queryOptions<ScimSettings, Error>({
                meta: {
                    subscribe: true,
                    type: 'resgate',
                    resourceId: topic,
                },
                queryKey: ['scim-settings', topic],
                queryFn: async (): Promise<ScimSettings> => {
                    const res = await ResgateManager.model<ScimSettings>(topic);
                    if (res instanceof ResErr) throw new Error(res.message);
                    return res;
                },
            });
        },
        getScimGroupMembers: (id: string): QueryOptions<Collection<ScimGroupMemberModel>, Error> => {
            const { instanceTopic } = useLoginStore();
            const topic = `v1.${instanceTopic}.sso.scim-role-mapping.${id}.members`;

            return queryOptions<Collection<ScimGroupMemberModel>, Error>({
                queryKey: ['scim-group-members', topic],
                queryFn: async (): Promise<Collection<ScimGroupMemberModel>> => {
                    const resgate = ResgateManager.getInstance();
                    const res = await resgate.collection<ScimGroupMemberModel>(topic);
                    if (res instanceof ResErr) throw new Error(res.message);
                    return res;
                },
            });
        },
        getScimGroupStatus: (): QueryOptions<{ total: number, notDone: number }, Error> => {
            const { instanceTopic } = useLoginStore();
            const topic = `v1.${instanceTopic}.sso.scim-role-mappings.status`;

            return queryOptions<{ total: number, notDone: number }, Error>({
                meta: {
                    subscribe: true,
                    type: 'resgate',
                    resourceId: topic,
                },
                queryKey: ['scim-group-status', topic],
                queryFn: async (): Promise<{ total: number, notDone: number }> => {
                    const res = await ResgateManager.model<{ total: number, notDone: number }>(topic);
                    if (res instanceof ResErr) throw new Error(res.message);
                    return res;
                },
            });
        },
        getScimRoleMappingFilterData: (): QueryOptions<ScimRoleMappingFilterData, Error> => {
            const { instanceTopic } = useLoginStore();

            return queryOptions<ScimRoleMappingFilterData, Error>({
                queryKey: ['scim-role-mapping-filter-data', instanceTopic],
                queryFn: async (): Promise<ScimRoleMappingFilterData> => {
                    return ssoService.get<ScimRoleMappingFilterData>(`v1/${instanceTopic}/scim-role-mappings/filterdata`);
                },
            });
        },
        getScimUserBasedFilterData: (): QueryOptions<ScimRoleMappingFilterData, Error> => {
            const { instanceTopic } = useLoginStore();

            return queryOptions<ScimRoleMappingFilterData, Error>({
                queryKey: ['scim-user-role-filter-data', instanceTopic],
                queryFn: async (): Promise<ScimRoleMappingFilterData> => {
                    return ssoService.get<ScimRoleMappingFilterData>(`v1/${instanceTopic}/scim-user-roles/filterdata`);
                },
            });
        },
    },
    mutations: {
        createScimApiKey: (): UseMutationOptions<ScimApiKey, Error, void, void> => {
            const { instanceTopic } = useLoginStore();

            return {
                mutationFn: async (): Promise<ScimApiKey> => {
                    const resgate = ResgateManager.getInstance();
                    const res = await resgate.call<ScimApiKey>(`v1.system.scim-api-keys.${instanceTopic}`, 'create', {});
                    if (res instanceof ResErr) throw new Error(res.message);
                    return res;
                },
            };
        },
        deleteScimApiKey: (): UseMutationOptions<void, Error, { id: string; }, void> => {
            const { instanceTopic } = useLoginStore();

            return {
                mutationFn: async ({ id }: { id: string; }): Promise<void> => {
                    const resgate = ResgateManager.getInstance();
                    const res = await resgate.call<void>(`v1.system.scim-api-key.${instanceTopic}.${id}`, 'delete', {});
                    if (res instanceof ResErr) throw new Error(res.message);
                },
            };
        },
        deleteScimIntegration: (): UseMutationOptions<void, Error, void, void> => {
            const { instanceTopic } = useLoginStore();

            return {
                mutationFn: async (): Promise<void> => {
                    const resgate = ResgateManager.getInstance();
                    const res = await resgate.call<void>(`v1.system.scim-api-keys.${instanceTopic}`, 'delete', {});
                    if (res instanceof ResErr) throw new Error(res.message);
                },
            };
        },
        updateScimMapping: (): UseMutationOptions<void, Error, { id: string; role?: Role | null; organisationNodeId?: string | null }, void> => {
            const { instanceTopic } = useLoginStore();
            const queryClient = useQueryClient();

            return {
                mutationFn: async ({ id, role, organisationNodeId }: { id: string; role?: Role | null; organisationNodeId?: string | null }): Promise<void> => {
                    const resgate = ResgateManager.getInstance();
                    const res = await resgate.call<void>(`v1.${instanceTopic}.sso.scim-role-mapping.${id}`, 'set', { role, organisationNodeId });
                    if (res instanceof ResErr) throw new Error(res.message);
                },
                onSettled: async (): Promise<void> => {
                    await queryClient.invalidateQueries({ queryKey: ['scim-role-mapping-filter-data'] });
                },
            };
        },
        bulkEditScimMapping: (): UseMutationOptions<void, Error, { ids: string[]; role?: Role | null; organisationNodeId?: string | null }, void> => {
            const { instanceTopic } = useLoginStore();
            const queryClient = useQueryClient();

            return {
                mutationFn: async ({ ids, role, organisationNodeId }): Promise<void> => {
                    const resgate = ResgateManager.getInstance();
                    const res = await resgate.call<void>(`v2.${instanceTopic}.sso.scim-role-mappings`, 'bulk', { ids, role, organisationNodeId });
                    if (res instanceof ResErr) throw new Error(res.message);
                },
                onSettled: async (): Promise<void> => {
                    await queryClient.invalidateQueries({ queryKey: ['scim-role-mapping-filter-data'] });
                },
            };
        },
        checkScimMappingDuplicate: (): UseMutationOptions<ScimRoleMappingModel | null, Error, { role: Role | null; organisationNodeId: string | null; roleMappingId: string }, void> => {
            const { instance } = useLoginStore();

            return {
                mutationFn: async (payload): Promise<ScimRoleMappingModel | null> => {
                    return ssoService.post<ScimRoleMappingModel | null>(payload, `v1/${instance}/scim-role-mapping/check`);
                },
            };
        },
        setScimSettings: (): UseMutationOptions<ScimSettings, Error, { roleProvisioningByScim: boolean }, void> => {
            const { instanceTopic } = useLoginStore();

            return {
                mutationFn: async ({ roleProvisioningByScim }: { roleProvisioningByScim: boolean }): Promise<ScimSettings> => {
                    const resgate = ResgateManager.getInstance();
                    const res = await resgate.call<ScimSettings>(`v1.scim-settings.${instanceTopic}`, 'set', { roleProvisioningByScim });
                    if (res instanceof ResErr) throw new Error(res.message);
                    return res;
                },
            };
        },
        setRoleProvisioningType: (): UseMutationOptions<ScimSettings, Error, { roleProvisioningType: RoleProvisioningType }, void> => {
            const { instanceTopic } = useLoginStore();

            return {
                mutationFn: async ({ roleProvisioningType }: { roleProvisioningType: RoleProvisioningType }): Promise<ScimSettings> => {
                    const resgate = ResgateManager.getInstance();
                    const res = await resgate.call<ScimSettings>(`v1.scim-settings.${instanceTopic}`, 'set', { roleProvisioningType });
                    if (res instanceof ResErr) throw new Error(res.message);
                    return res;
                },
            };
        },
        setMatchEmployeeAttribute: (): UseMutationOptions<ScimSettings, Error, { matchEmployeeAttribute: MatchEmployeeAttribute }, void> => {
            const { instanceTopic } = useLoginStore();

            return {
                mutationFn: async ({ matchEmployeeAttribute }: { matchEmployeeAttribute: MatchEmployeeAttribute }): Promise<ScimSettings> => {
                    const resgate = ResgateManager.getInstance();
                    const res = await resgate.call<ScimSettings>(`v1.scim-settings.${instanceTopic}`, 'set', { matchEmployeeAttribute });
                    if (res instanceof ResErr) throw new Error(res.message);
                    return res;
                },
            };
        },
    },
};
